import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSyncAlt } from "react-icons/fa";
import { downloadCSV, downloadPDF, printThermal } from "../utils/exportHelpers";
import { inventoryApi } from "../utils/inventoryApi";

const COLOR = {
  primary: "#015b9a",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
};

const InventoryItemPerformanceReportPage = () => {
  const [userId, setUserId] = useState(null);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(today());
  const [sort, setSort] = useState("qty");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [report, setReport] = useState({ items: [], totals: {} });
  const reportRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(user.id);
  }, []);

  useEffect(() => {
    if (userId) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fromDate, toDate, sort]);

  const fetchReport = async (opts = {}) => {
    if (!userId) return;
    opts.refresh ? setRefreshing(true) : setLoading(true);
    try {
      const data = await inventoryApi.getItemPerformanceReport({
        restaurantid: userId,
        from: fromDate,
        to: toDate,
        sort,
        limit: 100,
      });
      setReport({
        items: Array.isArray(data?.items) ? data.items : [],
        totals: data?.totals || {},
      });
    } catch (e) {
      toast.error(e.message || "Unable to load item performance report");
      setReport({ items: [], totals: {} });
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const fmtMoney = (n) =>
    `Rs ${Number(n || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const totals = report?.totals || {};
  const itemCount = useMemo(() => report.items.length, [report.items]);

  const exportCsv = () => {
    const rows = report.items.map((row, index) => [
      index + 1,
      row.name || "-",
      row.base_uom || "",
      row.used_qty ?? 0,
      row.used_value ?? 0,
      row.entries ?? 0,
      row.current_qty ?? 0,
    ]);
    downloadCSV(
      `inventory_item_performance_${fromDate}_to_${toDate}.csv`,
      ["#", "Item", "UOM", "Used Qty", "Used Value", "Entries", "Current Qty"],
      rows
    );
  };

  const exportPdf = () =>
    downloadPDF(
      reportRef.current,
      `inventory_item_performance_${fromDate}_to_${toDate}.pdf`
    );

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <Top>
        <div>
          <Title>Inventory Item Performance</Title>
          <Sub>Date-range usage and value contribution by item.</Sub>
          <PillRow>
            <Pill>Items: {itemCount}</Pill>
            <Pill>Total Used Qty: {totals.used_qty ?? 0}</Pill>
            <Pill>Total Used Value: {fmtMoney(totals.used_value)}</Pill>
          </PillRow>
        </div>
        <Actions>
          <DateInput>
            From:
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                const v = e.target.value;
                setFromDate(v);
                if (v > toDate) setToDate(v);
              }}
            />
          </DateInput>
          <DateInput>
            To:
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                const v = e.target.value;
                setToDate(v);
                if (v < fromDate) setFromDate(v);
              }}
            />
          </DateInput>
          <Select value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="qty">Sort: Quantity</option>
            <option value="value">Sort: Value</option>
          </Select>
          <PrimaryBtn type="button" onClick={() => fetchReport({ refresh: true })}>
            <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
          </PrimaryBtn>
        </Actions>
      </Top>

      <Card ref={reportRef}>
        {loading ? (
          <Center>Loading report...</Center>
        ) : report.items.length === 0 ? (
          <Center>No data found for selected range.</Center>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Item</th>
                  <th>UOM</th>
                  <th>Used Qty</th>
                  <th>Used Value</th>
                  <th>Entries</th>
                  <th>Current Qty</th>
                </tr>
              </thead>
              <tbody>
                {report.items.map((item, index) => (
                  <tr key={`${item.id || item.name || "row"}-${index}`}>
                    <td>{index + 1}</td>
                    <td>{item.name || "-"}</td>
                    <td>{item.base_uom || "-"}</td>
                    <td>{item.used_qty ?? 0}</td>
                    <td>{fmtMoney(item.used_value)}</td>
                    <td>{item.entries ?? 0}</td>
                    <td>{item.current_qty ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
        <Actions style={{ marginTop: 10 }}>
          <PrimaryBtn type="button" onClick={exportPdf}>
            PDF
          </PrimaryBtn>
          <PrimaryBtn
            type="button"
            onClick={() =>
              printThermal(reportRef.current, `inventory_item_performance_${fromDate}_to_${toDate}`)
            }
          >
            Thermal Print
          </PrimaryBtn>
          <PrimaryBtn type="button" onClick={exportCsv}>
            CSV
          </PrimaryBtn>
        </Actions>
      </Card>
    </Page>
  );
};

export default InventoryItemPerformanceReportPage;

const today = () => new Date().toISOString().slice(0, 10);

const Page = styled.div`
  background: ${COLOR.bg};
  min-height: 100vh;
  padding: 16px;
`;

const PillRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const Pill = styled.span`
  background: #eef2ff;
  color: ${COLOR.text};
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
  padding: 8px 10px;
  font-weight: 800;
`;

const Top = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 900;
  color: ${COLOR.text};
`;

const Sub = styled.div`
  color: ${COLOR.muted};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const DateInput = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: #f9f9f9;
  color: ${COLOR.text};
  input {
    border: 0;
    background: transparent;
    outline: none;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: #fff;
  color: ${COLOR.text};
  outline: none;
`;

const PrimaryBtn = styled.button`
  background: ${COLOR.primary};
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 10px 12px;
  font-weight: 800;
  cursor: pointer;
  display: inline-flex;
  gap: 8px;
  align-items: center;
`;

const Card = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 14px;
  padding: 12px;
`;

const TableWrap = styled.div`
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid #e5e7eb;
    text-align: left;
    font-size: 14px;
  }
  th {
    background: #f8fafc;
    font-weight: 800;
  }

  @media (max-width: 768px) {
    min-width: 640px;
    th,
    td {
      padding: 8px 10px;
      font-size: 12px;
      white-space: nowrap;
    }
  }
`;

const Center = styled.div`
  text-align: center;
  color: #6b7280;
  padding: 24px;
`;
